import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../api";
import { Alert, Card } from "../shell/ui";

export type BadgeLevel = "official" | "verified";

export interface LiveBadge {
  id: number;
  level: BadgeLevel;
  version: number;
  spec_hash: string;
  granted_at: string;
  note: string;
}

const LABEL: Record<BadgeLevel, string> = { official: "Official", verified: "Verified" };
const ICON: Record<BadgeLevel, string> = { official: "★", verified: "✓" };
const MEANING: Record<BadgeLevel, string> = {
  official: "A reference strategy maintained by StrategyHive",
  verified: "Reviewed by StrategyHive: re-run and reproduced, with enough trades to mean something",
};

/**
 * A strategy badge. Never colour alone: an icon and a word, and a title that
 * says what it vouches for and which version it is pinned to.
 */
export function StrategyBadge({ level, version }: { level: BadgeLevel; version?: number }) {
  return (
    <span
      className={`badge-level badge-level--${level}`}
      title={`${MEANING[level]}${version ? ` (v${version} exactly)` : ""}`}
    >
      <span aria-hidden="true">{ICON[level]}</span> {LABEL[level]}
      {version ? ` v${version}` : ""}
    </span>
  );
}

/** The small mark after a Verified publisher's name. */
export function PublisherMark({ badge }: { badge?: "verified" | null }) {
  if (badge !== "verified") return null;
  return (
    <span
      className="publisher-mark"
      title="Verified publisher: a member StrategyHive vouches for"
      role="img"
      aria-label="Verified publisher"
    >
      ✓
    </span>
  );
}

/**
 * The badges on a strategy, and the honest caveat when the newest version is not
 * the one that was reviewed.
 */
export function StrategyBadges({ badges, latestVersion }: { badges: LiveBadge[]; latestVersion: number }) {
  if (badges.length === 0) return null;
  const top = badges[0]!;
  const newer = latestVersion > top.version;
  return (
    <span className="badge-row">
      {badges.map((b) => (
        <StrategyBadge key={b.id} level={b.level} version={b.version} />
      ))}
      {newer && <span className="muted small">v{latestVersion} not reviewed yet</span>}
    </span>
  );
}

interface BadgeCheck {
  id: string;
  label: string;
  ok: boolean;
  required: boolean;
  detail?: string;
}

interface BadgeState {
  badges: LiveBadge[];
  checks: BadgeCheck[] | null;
  version: number | null;
  request: null | {
    id: number;
    status: "pending" | "approved" | "rejected" | "withdrawn";
    review_note: string;
    created_at: string;
    decided_at: string | null;
    version: number;
  };
}

export function CheckList({ checks }: { checks: BadgeCheck[] }) {
  return (
    <ul className="checklist">
      {checks.map((c) => (
        <li key={c.id} className={c.ok ? "is-ok" : c.required ? "is-fail" : "is-warn"}>
          <span aria-hidden="true">{c.ok ? "✓" : c.required ? "✕" : "!"}</span>
          <span>
            {c.label}
            {!c.required && <span className="muted small"> · advisory</span>}
            {c.detail && <span className="muted small"> · {c.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The owner's side of verification: what the machine checks, the request, and its outcome. */
export function VerificationPanel({
  strategyId,
  headlineRunId,
}: {
  strategyId: string;
  headlineRunId?: string | null;
}) {
  const qc = useQueryClient();
  const [message, setMessage] = useState("");
  const state = useQuery({
    queryKey: ["hub-badges", strategyId],
    queryFn: () => api<BadgeState>(`/hub/strategies/${strategyId}/badges`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["hub-badges", strategyId] });
    void qc.invalidateQueries({ queryKey: ["hub-strategy", strategyId] });
  };
  const request = useMutation({
    mutationFn: () =>
      api(`/hub/strategies/${strategyId}/badge-request`, { method: "POST", body: { message } }),
    onSuccess: refresh,
  });
  const withdraw = useMutation({
    mutationFn: () => api(`/hub/strategies/${strategyId}/badge-request/withdraw`, { method: "POST" }),
    onSuccess: refresh,
  });
  const d = state.data;
  if (!d?.checks) return null;
  const pending = d.request?.status === "pending";
  const verifiedLatest = d.badges.some((b) => b.version === d.version);
  const ready = d.checks.every((c) => c.ok || !c.required);
  return (
    <Card title="Verification">
      <div className="stack">
        <p className="small muted">
          A <strong>Verified</strong> badge says StrategyHive re-ran this exact version and reviewed it. It is
          pinned to one version: publishing a new one needs a new review.
        </p>
        {verifiedLatest ? (
          <Alert tone="ok">v{d.version} is verified.</Alert>
        ) : (
          <>
            <CheckList checks={d.checks} />
            {d.checks.some((c) => c.id === "reproduced" && !c.ok) && headlineRunId && (
              <p className="small">
                <Link to={`/r/${headlineRunId}`}>Open the backtest</Link> and press <em>Re-run to verify</em>.
              </p>
            )}
            {d.request?.status === "rejected" && (
              <Alert tone="error">
                The last request (v{d.request.version}) was not approved: {d.request.review_note}
              </Alert>
            )}
            {pending ? (
              <div className="row">
                <span className="badge">
                  Waiting for review since {new Date(d.request!.created_at).toLocaleDateString()}
                </span>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={withdraw.isPending}
                  onClick={() => withdraw.mutate()}
                >
                  Withdraw
                </button>
              </div>
            ) : (
              <>
                <textarea
                  rows={2}
                  maxLength={1000}
                  placeholder="A note for the reviewer (optional)"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  aria-label="Note for the reviewer"
                />
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!ready || request.isPending}
                  onClick={() => request.mutate()}
                  title={ready ? undefined : "Every required check has to pass first"}
                >
                  Request verification of v{d.version}
                </button>
              </>
            )}
            {(request.error || withdraw.error) && (
              <Alert tone="error">{(request.error ?? withdraw.error)!.message}</Alert>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
